/**
 * GenerateView - Card generation workflow
 *
 * Flow: Input (image upload + text) → Generate → Preview Stack → Reset
 *
 * AI auto-detects card types and generates 1-5 cards per request.
 * Results are shown as a swipeable card stack.
 */

import { useState, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { generateCard } from '@/lib/api-client'
import {
  compressImage,
  convertHeicToBlob,
  generatePreview,
  isHeicFormat,
  isValidImageFile,
  isValidFileSize,
  formatFileSize,
  MAX_FILE_SIZE,
} from '@/lib/image-utils'
import { CardStack } from '@/components/ui/CardStack'
import type { GeneratedCard } from '@shared/schemas'
import { ImageTypeSchema } from '@shared/schemas'
import { deleteCard } from '@/lib/api-client'

const INTENT_CHIPS = [
  { label: '单词', template: '' },
  { label: '批量列表', template: '的英语怎么说' },
  { label: '翻译', template: '这句话英语怎么说：' },
  { label: '地道表达', template: '有什么地道的英语表达' },
  { label: '易混淆词', template: 'vs' },
  { label: '词族', template: '的派生词有哪些' },
  { label: '语法', template: '怎么用' },
  { label: '多义词', template: '的不同意思和用法' },
] as const

interface ValidationError {
  type: 'image' | 'text' | 'network'
  message: string
}

export function GenerateView() {
  const location = useLocation()
  const prefillText = (location.state as { prefillText?: string } | null)?.prefillText || ''

  // Input state
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [text, setText] = useState(prefillText)
  const [isCompressing, setIsCompressing] = useState(false)
  const [isConvertingHeic, setIsConvertingHeic] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedCards, setGeneratedCards] = useState<GeneratedCard[]>([])
  const [cardIds, setCardIds] = useState<string[]>([])
  const [error, setError] = useState<ValidationError | null>(null)
  const canGenerate = Boolean(imageFile || text.trim())

  // Handle image file selection
  const handleImageSelect = useCallback(async (file: File) => {
    setError(null)

    if (!isValidImageFile(file)) {
      setError({ type: 'image', message: 'Please upload an image (JPEG, PNG, GIF, WebP, or HEIC)' })
      return
    }

    if (!isValidFileSize(file)) {
      setError({ type: 'image', message: `Image must be less than ${formatFileSize(MAX_FILE_SIZE)}` })
      return
    }

    const needsHeic = isHeicFormat(file)
    if (needsHeic) {
      setIsConvertingHeic(true)
    } else {
      setIsCompressing(true)
    }

    try {
      let processed = file
      if (needsHeic) {
        const blob = await convertHeicToBlob(file)
        processed = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpeg'), { type: 'image/jpeg' })
      }

      const compressed = await compressImage(processed)
      const ext = compressed.type === 'image/webp' ? 'webp' : 'jpg'
      const compressedFile = new File([compressed], processed.name.replace(/\.[^.]+$/, '.' + ext), { type: compressed.type || 'image/jpeg' })

      // Post-conversion size check — HEIC→JPEG can still produce large files
      if (!isValidFileSize(compressedFile)) {
        setError({ type: 'image', message: `Image is still too large after compression. Try a smaller photo.` })
        return
      }

      const preview = await generatePreview(compressedFile)

      setImageFile(compressedFile)
      setImagePreview(preview)
    } catch (err) {
      setError({ type: 'image', message: err instanceof Error ? err.message : 'Failed to process image' })
    } finally {
      setIsCompressing(false)
      setIsConvertingHeic(false)
    }
  }, [])

  // Drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleImageSelect(files[0])
  }, [handleImageSelect])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  // Remove image
  const handleRemoveImage = useCallback(() => {
    setImageFile(null)
    setImagePreview('')
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  // Reset to input view
  const resetView = useCallback(() => {
    setGeneratedCards([])
    setCardIds([])
    setError(null)
    setImageFile(null)
    setImagePreview('')
    setText('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  // Generate cards
  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return

    setIsGenerating(true)
    setError(null)
    setGeneratedCards([])

    try {
      const formData = new FormData()
      if (imageFile) formData.append('image', imageFile)
      if (text.trim()) formData.append('text', text.trim())

      const response = await generateCard(formData)

      const cards: GeneratedCard[] = response.cards.map(card => {
        const parsedType = ImageTypeSchema.safeParse(card.type)
        return {
          front: card.front,
          back: card.back,
          definition: card.definition,
          example: card.example,
          pos: card.pos,
          phonetic_us: card.phonetic_us || '',
          phonetic_uk: card.phonetic_uk || '',
          collocations: card.collocations || [],
          tags: card.tags || [],
          type: parsedType.success ? parsedType.data : 'OBJECT',
        }
      })

      // response.cards are full Card records from DB (include id), GeneratedCard type omits id
      const ids = (response.cards as Array<{id?: string}>).map(card => card.id || '')
      setGeneratedCards(cards)
      setCardIds(ids)
    } catch (err) {
      setError({
        type: 'network',
        message: err instanceof Error ? err.message : 'Failed to generate cards',
      })
    } finally {
      setIsGenerating(false)
    }
  }, [imageFile, text, canGenerate])

  // Edit card (from CardStack)
  const handleEdit = useCallback((index: number, card: GeneratedCard) => {
    setGeneratedCards(prev => {
      const next = [...prev]
      next[index] = card
      return next
    })
  }, [])

  // Discard card (from CardStack) — delete from database
  const handleDiscard = useCallback((index: number) => {
    const cardId = cardIds[index]
    setGeneratedCards(prev => prev.filter((_, i) => i !== index))
    setCardIds(prev => prev.filter((_, i) => i !== index))
    if (cardId) {
      deleteCard(cardId).catch(err => {
        console.error('Failed to delete card:', err)
      })
    }
  }, [cardIds])

  // ---- Render ----

  // If we have generated cards, show card stack
  if (generatedCards.length > 0) {
    return (
      <div className="px-4 py-4 flex flex-col gap-4">
        <CardStack
          cards={generatedCards}
          imageUrl={imagePreview || undefined}
          onEdit={handleEdit}
          onDiscard={handleDiscard}
          onDone={resetView}
        />
      </div>
    )
  }

  // Input view
  return (
    <div className="px-4 py-4 flex flex-col gap-4">
      {/* Error display */}
      {error && (
        <div className="bg-error-container rounded-2xl p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-error text-[20px] mt-0.5">error</span>
          <div className="flex-1">
            <p className="text-sm text-on-error-container">{error.message}</p>
            {error.type === 'network' && (
              <button onClick={handleGenerate} className="text-xs font-bold text-on-error-container mt-1">
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {/* Image Upload */}
      <div>
        <h3 className="font-headline font-bold text-xs text-on-surface-variant uppercase tracking-wide mb-2">
          Image
        </h3>
        {imagePreview ? (
          <div className="relative rounded-2xl overflow-hidden bg-surface-container-low">
            <img src={imagePreview} alt="Preview" className="w-full max-h-48 object-contain" />
            <button
              onClick={handleRemoveImage}
              className="absolute top-3 right-3 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center"
              aria-label="Remove image"
            >
              <span className="material-symbols-outlined text-white text-[18px]">close</span>
            </button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-outline-variant/40 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary/40 hover:bg-surface-container-low/50 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={e => {
                const files = e.target.files
                if (files && files.length > 0) handleImageSelect(files[0])
              }}
              className="hidden"
              aria-label="Upload image"
            />
            <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-2">cloud_upload</span>
            <p className="text-sm text-on-surface-variant text-center">
              Drag & drop or tap to upload
            </p>
            {isConvertingHeic && (
              <p className="text-xs text-primary mt-2 animate-pulse">Converting HEIC...</p>
            )}
            {isCompressing && (
              <p className="text-xs text-primary mt-2 animate-pulse">Compressing...</p>
            )}
          </div>
        )}
      </div>

      {/* Text Input */}
      <div>
        <h2 className="font-headline font-bold text-lg text-on-surface mb-2">
          今天想学什么？
        </h2>
        <textarea
          rows={5}
          placeholder="输入单词、句子、或问题..."
          value={text}
          onChange={e => { setText(e.target.value); setError(null) }}
          className="w-full px-4 py-3 bg-surface-container rounded-[1.5rem] text-on-surface text-base focus:outline-none focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary/30 resize-none"
          aria-label="Card text input"
        />

        {/* Intent Chips */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          {INTENT_CHIPS.map(chip => (
            <button
              key={chip.label}
              type="button"
              onClick={() => {
                const template = chip.template
                setText(prev => prev ? `${prev} ${template}` : template)
              }}
              className="shrink-0 px-4 py-1.5 bg-surface-soft text-muted text-[12px] font-bold tracking-[0.05em] uppercase rounded-full hover:bg-surface-card active:scale-95 transition-all"
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={!canGenerate || isGenerating}
        className={`w-full py-3.5 rounded-full font-bold text-sm shadow-card transition-opacity ${
          canGenerate && !isGenerating
            ? 'bg-primary text-on-primary'
            : 'bg-surface-container text-on-surface-variant opacity-50'
        }`}
      >
        {isGenerating ? (
          <span className="flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
            Generating...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
            Generate Cards
          </span>
        )}
      </button>
    </div>
  )
}
